import type { ReactNode } from 'react';
import type { MenuEntry } from '@viquard/doc-ui/components';
import type { CalendarEvent } from '../types.js';

export interface EventMenuLabels {
  readonly open?: string;
  readonly edit?: string;
  readonly duplicate?: string;
  readonly moveToToday?: string;
  readonly cancel?: string;
  readonly uncancel?: string;
  readonly delete?: string;
}

export interface EventMenuIcons {
  readonly open?: ReactNode;
  readonly edit?: ReactNode;
  readonly duplicate?: ReactNode;
  readonly moveToToday?: ReactNode;
  readonly cancel?: ReactNode;
  readonly delete?: ReactNode;
}

export interface EventMenuActions {
  readonly onOpen?: ((event: CalendarEvent) => void) | undefined;
  readonly onEdit?: ((event: CalendarEvent) => void) | undefined;
  readonly onDuplicate?: ((event: CalendarEvent) => void) | undefined;
  readonly onMoveToToday?: ((event: CalendarEvent) => void) | undefined;
  readonly onCancel?: ((event: CalendarEvent) => void) | undefined;
  readonly onDelete?: ((event: CalendarEvent) => void) | undefined;
  readonly labels?: EventMenuLabels | undefined;
  readonly icons?: EventMenuIcons | undefined;
}

export interface BuildEventMenuOptions extends EventMenuActions {
  readonly event: CalendarEvent;
  /** Override per-action labels (e.g. for i18n). */
  readonly labels?: EventMenuLabels | undefined;
  /** Optional icons keyed by action id. */
  readonly icons?: EventMenuIcons | undefined;
}

/**
 * Produce a doc-ui MenuEntry[] from a CalendarEvent + the consumer's
 * action handlers. Entries are omitted when their handler isn't
 * provided, so the menu is naturally context-aware.
 *
 * Order: Open → Edit → Duplicate → Move to today → (separator) →
 * Cancel/Uncancel → Delete (danger).
 */
export function buildEventContextMenu(options: BuildEventMenuOptions): readonly MenuEntry[] {
  const { event, labels = {}, icons = {} } = options;
  const items: MenuEntry[] = [];

  if (options.onOpen !== undefined) {
    items.push({
      kind: 'item',
      id: 'open',
      label: labels.open ?? 'Open',
      ...(icons.open !== undefined ? { icon: icons.open } : {}),
      onSelect: () => options.onOpen?.(event),
    });
  }
  if (options.onEdit !== undefined) {
    items.push({
      kind: 'item',
      id: 'edit',
      label: labels.edit ?? 'Edit…',
      shortcut: 'E',
      ...(icons.edit !== undefined ? { icon: icons.edit } : {}),
      onSelect: () => options.onEdit?.(event),
    });
  }
  if (options.onDuplicate !== undefined) {
    items.push({
      kind: 'item',
      id: 'duplicate',
      label: labels.duplicate ?? 'Duplicate',
      shortcut: '⌘D',
      ...(icons.duplicate !== undefined ? { icon: icons.duplicate } : {}),
      onSelect: () => options.onDuplicate?.(event),
    });
  }
  if (options.onMoveToToday !== undefined) {
    items.push({
      kind: 'item',
      id: 'moveToToday',
      label: labels.moveToToday ?? 'Move to today',
      ...(icons.moveToToday !== undefined ? { icon: icons.moveToToday } : {}),
      onSelect: () => options.onMoveToToday?.(event),
    });
  }

  const hasMutators =
    options.onCancel !== undefined || options.onDelete !== undefined;
  if (items.length > 0 && hasMutators) {
    items.push({ kind: 'separator', id: 'sep-1' });
  }

  if (options.onCancel !== undefined) {
    const isCancelled = event.status === 'cancelled';
    items.push({
      kind: 'item',
      id: 'cancel',
      label: isCancelled
        ? (labels.uncancel ?? 'Uncancel')
        : (labels.cancel ?? 'Cancel event'),
      ...(icons.cancel !== undefined ? { icon: icons.cancel } : {}),
      onSelect: () => options.onCancel?.(event),
    });
  }
  if (options.onDelete !== undefined) {
    items.push({
      kind: 'item',
      id: 'delete',
      label: labels.delete ?? 'Delete',
      tone: 'danger',
      shortcut: '⌫',
      ...(icons.delete !== undefined ? { icon: icons.delete } : {}),
      onSelect: () => options.onDelete?.(event),
    });
  }

  return items;
}
